// MissionsIndex.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
  getCountFromServer,
} from "firebase/firestore";
import { db } from "../../firebase";
import "./css/MissionsIndex.css";
import ToolbarHeader from "./components/ToolbarHeader";
import { GTA_GAME_TABS } from "./components/ToolbarHeader";
import MissionForm from "../Profile/MissionForm"; // adjust path if needed
import Modal from "./components/Modal";

export default function MissionsIndex({ user }) {
  // 1) state
  const [missions, setMissions] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState("main");
  const [commentCounts, setCommentCounts] = useState({});
  const [game, setGame] = useState("gta-5");
  const isSuperAdmin = user?.role === "superadmin";
  const isAdmin = isSuperAdmin || user?.role === "admin";

  // 2) helpers (keep here)
  const norm = (s) => (s || "").toString().trim().toLowerCase();
  const isMain = (m) => ["main", "main story", "story", "campaign"].includes(norm(m.category));
  const isSide = (m) => ["side", "side mission", "secondary"].includes(norm(m.category));
  const isMine = (m, user) => {
    const uid = user?.uid;
    return !!uid && [m.createdBy, m.userId, m.authorId, m.ownerId].some((x) => x === uid);
  };

  // 3) list memo (👈 must be before any effect that uses `list`)
  const list = useMemo(() => {
    let arr = missions.filter(
      (m) => m.createdByRole === "admin" && m.fanCreated === false
    );


    // 👑 MY MISSIONS OVERRIDE (IMPORTANT)
    if (filter === "mine") {
      arr = arr.filter((m) => isMine(m, user));
    } else {
      // 🔒 Privacy filter for all other views
      arr = arr.filter((m) => !m.isPrivate || isMine(m, user));

      // 🎮 Game filter (ignored for "mine")
      if (game && game !== "all") {
        arr = arr.filter((m) => m.gameKey === game);
      }

      // 📂 Category filter
      if (filter === "main") {
        arr = arr.filter((m) => isMain(m));
      }

      if (filter === "side") {
        arr = arr.filter((m) => isSide(m));
      }

      if (filter === "fan") {
        arr = arr.filter((m) => m.fanCreated === true);
      }

    }

    // 🔍 SEARCH (applies to all, including mine)
    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter((m) =>
        [m.missionName, m.location, ...(m.tags || []), m.category]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(q)
      );
    }

    // 🔢 SORT
    arr.sort((a, b) => {
      const rank = () => 0; // all remaining missions are admin
      const r = rank(a) - rank(b);
      if (r !== 0) return r;

      const an = a.missionNumber ?? Infinity;
      const bn = b.missionNumber ?? Infinity;
      if (an !== bn) return an - bn;

      return (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0);
    });


    return arr;
  }, [missions, search, filter, game, user]);

  const missionsByGame = useMemo(() => {
    const groups = {};
    list.forEach((m) => {
      const key = m.gameKey || "unknown";
      if (!groups[key]) groups[key] = [];
      groups[key].push(m);
    });
    return groups;
  }, [list]);

  const buildMissionTree = (missions) => {
    const parents = [];
    const byParentId = {};

    missions.forEach((m) => {
      if (m.isVariant && m.parentMissionId) {
        if (!byParentId[m.parentMissionId]) {
          byParentId[m.parentMissionId] = [];
        }
        byParentId[m.parentMissionId].push(m);
      } else {
        parents.push(m);
      }
    });

    // sort variants (11.1, 11.2)
    Object.values(byParentId).forEach((arr) =>
      arr.sort((a, b) => (a.subMissionNumber ?? 0) - (b.subMissionNumber ?? 0))
    );

    // sort parents by mission number
    parents.sort(
      (a, b) => (a.missionNumber ?? 999) - (b.missionNumber ?? 999)
    );

    return parents.map((p) => ({
      parent: p,
      variants: byParentId[p.arcId] || [],
    }));
  };


  // 4) effects can now use `list` safely
  useEffect(() => {
    if (!list.length) {
      setCommentCounts({});
      return;
    }
    let cancelled = false;
    (async () => {
      const results = await Promise.all(
        list.map(async (m) => {
          try {
            const q = query(
              collection(db, "missions", m.id, "threads"),  // ⬅️ subcollection, not root
              where("type", "==", "comment")
            );
            const snap = await getCountFromServer(q);
            return [m.id, snap.data().count];
          } catch (e) {
            console.warn("count() failed for", m.id, e);
            return [m.id, undefined];
          }

        })
      );
      if (!cancelled) setCommentCounts(Object.fromEntries(results));
    })();
    return () => {
      cancelled = true;
    };
  }, [list]);


  // Load missions
  useEffect(() => {
    // helper to merge snapshots by id
    const mergeById = (...lists) => {
      const map = new Map();
      lists.flat().forEach((m) => map.set(m.id, m));
      // sort by createdAt desc (fallback 0)
      return [...map.values()].sort(
        (a, b) =>
          (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0)
      );
    };

    setLoading(true);

    // 1) Public missions
    const qPublic = query(
      collection(db, "missions"),
      where("isPrivate", "==", false),
      orderBy("createdAt", "desc")
    );

    let publicDocs = [];
    let mineDocs = [];
    const unsubs = [];

    const unsubPublic = onSnapshot(qPublic, (snap) => {
      publicDocs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setMissions((prev) => mergeById(publicDocs, mineDocs));
      setLoading(false);
    });
    unsubs.push(unsubPublic);

    // 2) My missions (may include private). Pick ONE authoritative owner field.
    // If your schema varies, prefer normalizing to `ownerId` on write.
    if (user?.uid) {
      // If you *must* support legacy fields, you can add extra listeners:
      // ownerId==uid OR createdBy==uid (FireStore doesn't support OR on fields)
      // so we listen to both and merge.
      const qs = [
        query(
          collection(db, "missions"),
          where("ownerId", "==", user.uid),
          orderBy("createdAt", "desc")
        ),
        query(
          collection(db, "missions"),
          where("createdBy", "==", user.uid),
          orderBy("createdAt", "desc")
        ),
      ];

      qs.forEach((qq) => {
        const u = onSnapshot(qq, (snap) => {
          // merge *these* "mine" results together first
          const batch = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          // merge into mineDocs by id (so ownerId & createdBy sets don’t duplicate)
          const tmp = new Map(mineDocs.map((m) => [m.id, m]));
          batch.forEach((m) => tmp.set(m.id, m));
          mineDocs = [...tmp.values()];

          // now merge with public and push to state
          setMissions(mergeById(publicDocs, mineDocs));
          setLoading(false);
        });
        unsubs.push(u);
      });
    }

    return () => unsubs.forEach((u) => u && u());
  }, [db, user?.uid]);


  return (
    <>
      <div className="msn-wrap">
        <ToolbarHeader
          search={search}
          setSearch={setSearch}
          placeholder="Search missions, tags, location…"
          onAddMission={() => setShowModal(true)}
          user={user}
          addLabel="＋ Add Mission"
        >
          {/* 🎮 GAME FILTER (TEXT TABS) */}
          <div className="forum-tabs forum-tabs--games">
            {GTA_GAME_TABS.filter(g => g.key !== "all").map((g) => (
              <span
                key={g.key}
                className={`tab-text ${game === g.key ? "active" : ""}`}
                onClick={() => setGame(g.key)}
              >
                {g.label}
              </span>
            ))}
          </div>

          {/* 📂 CATEGORY FILTER */}
          <div className="forum-tabs">
            {["main", "side", "fan"].concat(user ? ["mine"] : [])
              .map((t) => (
                <span
                  key={t}
                  className={`tab-text ${filter === t ? "active" : ""}`}
                  onClick={() => setFilter(t)}
                >
                  {t === "main"
                    ? "Main Story"
                    : t === "side"
                      ? "Side Missions"
                      : t === "fan"
                        ? "Fan Missions"
                        : "My Missions"}

                </span>
              ))}
          </div>

        </ToolbarHeader>

        <ul className="msn-layout">
          {loading &&
            Array.from({ length: 6 }).map((_, i) => (
              <li key={`sk-${i}`} className="msn-card msn-card--skeleton">
                <div className="msn-thumb skeleton" />
                <div className="msn-card-body">
                  <div className="skeleton skeleton-line" />
                  <div className="skeleton skeleton-line short" />
                </div>
              </li>
            ))}

          {!loading &&
            Object.entries(missionsByGame).map(([gameKey, missions]) => {
              const gameLabel =
                GTA_GAME_TABS.find((g) => g.key === gameKey)?.label || "Other";

              const tree = buildMissionTree(missions);

              if (!tree.length) return null;

              return (
                <React.Fragment key={gameKey}>
                  {/* 🎮 GAME HEADER */}
                  <li className="msn-game-header">🎮 {gameLabel}</li>

                  {/* 🛡 ADMIN MISSIONS */}
                  {tree.map(({ parent, variants }) => (
                    <React.Fragment key={parent.id}>
                      {/* ===== PARENT ===== */}
                      <li className="msn-admin-item">
                        <Link to={`/community/missions/${parent.id}`} state={{ user }}>
                          <div className="msn-admin-row">
                            <div className="msn-admin-titleline">
                              {parent.missionNumber != null && (
                                <span className="msn-admin-num">
                                  #{parent.missionNumber}
                                </span>
                              )}
                              {parent.missionName}
                            </div>

                            <div className="msn-admin-desc">
                              {parent.category} · {parent.location}
                              {parent.shortNote && ` — ${parent.shortNote}`}
                            </div>
                          </div>
                        </Link>
                      </li>

                      {/* ===== VARIANTS ===== */}
                      {variants.map((v) => (
                        <li
                          key={v.id}
                          className="msn-admin-item msn-admin-item--variant"
                        >
                          <Link to={`/community/missions/${v.id}`} state={{ user }}>
                            <div className="msn-admin-row">
                              <div className="msn-admin-titleline">
                                <span className="msn-admin-num msn-admin-num--sub">
                                  {v.displayNumber}
                                </span>
                                {v.missionName
                                  .replace(parent.missionName, "")
                                  .trim()}
                              </div>

                              <div className="msn-admin-desc">
                                {v.variantType?.toUpperCase()} · {v.location}
                                {v.shortNote && ` — ${v.shortNote}`}
                              </div>
                            </div>
                          </Link>
                        </li>
                      ))}
                    </React.Fragment>
                  ))}
                </React.Fragment>
              );
            })}

          {!loading && list.length === 0 && (
            <li className="msn-empty">No missions match your search.</li>
          )}
        </ul>

      </div>

      {/* Reusable Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title="Submit a Mission"
        size="md" // "sm" | "md" | "lg" | number (percentage)
        closeOnBackdrop={true}
        showClose={true}
      >
        <MissionForm
          user={user}
          enforceAdmin={false}
          isAdmin={false}
          onSaved={() => setShowModal(false)}
          onCancel={() => setShowModal(false)}
          initial={{ difficulty: "Normal" }}
          mode="create"
        />
      </Modal>
    </>
  );
}
